import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { FilterChipItem, FilterChipList } from '@/components/FilterChipList';
import { Box } from '@/components/lib/components';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';

import { FinishedTab } from '../hooks/types';
import { BetListContent } from './BetList';

/**
 * Finished-tab filter chips. `tab` is the backend `tab` value; `tabType` is the lowercase noun
 * interpolated into the empty-state copy (`No {{tabType}} bets`). Labels reuse the `bets` namespace —
 * `all` plus the shared `entry_status_badge.*` keys. Predictions relabels the cashed-out chip
 * "Sold"; the backend tab value stays `CashedOut`.
 */
type FinishedFilter = {
    tab: FinishedTab;
    labelKey: 'all' | `entry_status_badge.${'won' | 'lost' | 'cashedOut' | 'sold' | 'refunded'}`;
    tabType: string;
};

const SWEEPS_FINISHED_FILTERS: readonly FinishedFilter[] = [
    { tab: 'All', labelKey: 'all', tabType: 'finished' },
    { tab: 'Won', labelKey: 'entry_status_badge.won', tabType: 'won' },
    { tab: 'Lost', labelKey: 'entry_status_badge.lost', tabType: 'lost' },
    { tab: 'CashedOut', labelKey: 'entry_status_badge.cashedOut', tabType: 'cashed out' },
    { tab: 'Refunded', labelKey: 'entry_status_badge.refunded', tabType: 'refunded' },
];

const PREDICTIONS_FINISHED_FILTERS: readonly FinishedFilter[] = SWEEPS_FINISHED_FILTERS.map(filter =>
    filter.tab === 'CashedOut' ? { tab: 'CashedOut', labelKey: 'entry_status_badge.sold', tabType: 'sold' } : filter
);

export const FinishedBetList = React.memo(() => {
    const { t } = useTranslation('bets');
    const isPredictions = useIsPredictionsProduct();
    const [selected, setSelected] = useState<FinishedTab>('All');

    const filters = isPredictions ? PREDICTIONS_FINISHED_FILTERS : SWEEPS_FINISHED_FILTERS;
    const activeFilter = filters.find(filter => filter.tab === selected) ?? filters[0];

    const chipItems = useMemo<FilterChipItem<FinishedTab>[]>(
        () =>
            filters.map(filter => ({
                id: filter.tab,
                label: t(filter.labelKey),
                testID: `finishedFilter-${filter.tab}`,
            })),
        [t, filters]
    );

    const chipRow = (
        <Box paddingTop="s16" paddingBottom="s24">
            <FilterChipList
                items={chipItems}
                selected={selected}
                onSelect={setSelected}
                contentContainerStyle={styles.chipListContent}
            />
        </Box>
    );

    return <BetListContent tab={selected} tabType={activeFilter.tabType} listHeaderComponent={chipRow} />;
});

const styles = StyleSheet.create({
    chipListContent: {
        paddingLeft: 16,
    },
});
